import { Alert, Button, Flex, Space, Spin, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import {
  getDefaultBuildingFloorOptions,
  readBuildingFloorLabel,
  readBuildingFloorValue,
} from '@/features/inventorization/building-floor-options';
import {
  FLOORPLAN_PLACEMENT_STATES,
  type InventorizationPosition,
} from '@/features/inventorization/domain/inventorization-position';
import type { InventoryBuildingOption } from '@/features/inventorization/new-meter-location-client';
import { OUTSIDE_BUILDING_SELECT_VALUE } from '@/features/inventorization/placement-options';
import {
  isSameBuilding,
  isSameFloor,
  type PlanPoint,
  planPointToFloorplanCoordinates,
  readFloorplanCoordinatesPoint,
  readFloorplanDimensions,
  readMeterFloorplanPoint,
} from '@/features/inventorization/position/floorplan-marker-geometry';
import { useNewMeterLocationStep } from '@/features/inventorization/useNewMeterLocationStep';
import { useLocationBootstrap } from '@/features/location-bootstrap/LocationBootstrapContext';
import { FloorplanPlacementPanel } from '@/routers/inventorization/components/position/FloorplanPlacementPanel';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

const GROUND_FLOOR_VALUE = '0';

interface NewMeterLocationStepProps {
  onBack: () => void;
  onNext: (patch: Partial<InventorizationPosition>) => Promise<void>;
  position: InventorizationPosition;
}

export function NewMeterLocationStep({ onBack, onNext, position }: NewMeterLocationStepProps) {
  const { activeLocation } = useStartupSession();
  const { bootstrap } = useLocationBootstrap();
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null | undefined>(() =>
    position.buildingId == null ? (position.floor ? null : undefined) : String(position.buildingId),
  );
  const [selectedFloor, setSelectedFloor] = useState(readBuildingFloorValue(position.floor));
  const [selectedFloorplanMarker, setSelectedFloorplanMarker] = useState<PlanPoint | null>(null);
  const [floorplanUploadError, setFloorplanUploadError] = useState<string | null>(null);
  const [isSavingFloorplan, setIsSavingFloorplan] = useState(false);
  const {
    buildings,
    error,
    floorplan,
    floorplanImageUrl,
    floorplanRecord,
    isLoadingBuildings,
    isLoadingFloorplan,
    saveLocalFloorplan,
  } = useNewMeterLocationStep({
    activeLocation,
    buildingId: selectedBuildingId ?? null,
    floor: selectedBuildingId === undefined ? '' : selectedFloor,
  });
  const selectedBuilding = useMemo(
    () => buildings.find(building => building.id === selectedBuildingId) ?? null,
    [buildings, selectedBuildingId],
  );
  const selectedFloorLabel = useMemo(
    () => (selectedFloor ? readBuildingFloorLabel(selectedFloor) : ''),
    [selectedFloor],
  );
  const floorplanDimensions = useMemo(() => readFloorplanDimensions(floorplanRecord), [floorplanRecord]);
  const existingFloorplanPins = useMemo(
    () =>
      (bootstrap?.existingMeters ?? [])
        .filter(meter => isSameFloor(meter.floorName, selectedFloor, selectedFloorLabel))
        .filter(meter => isSameBuilding(meter, selectedBuildingId ?? null))
        .map(meter => readMeterFloorplanPoint(meter, floorplanDimensions))
        .filter((point): point is PlanPoint => Boolean(point)),
    [bootstrap?.existingMeters, floorplanDimensions, selectedBuildingId, selectedFloor, selectedFloorLabel],
  );
  const isOutsideBuilding = selectedBuildingId === null;
  const canContinue = Boolean(selectedFloor) && selectedBuildingId !== undefined && !isSavingFloorplan;

  useEffect(() => {
    if (selectedBuildingId !== undefined || !position.building) {
      return;
    }

    const matchingBuilding = buildings.find(building => building.name === position.building);

    if (matchingBuilding) {
      setSelectedBuildingId(matchingBuilding.id);
    }
  }, [buildings, position.building, selectedBuildingId]);

  useEffect(() => {
    if (isOutsideBuilding && selectedFloor !== GROUND_FLOOR_VALUE) {
      setSelectedFloor(GROUND_FLOOR_VALUE);
      setSelectedFloorplanMarker(null);
      setFloorplanUploadError(null);
    }
  }, [isOutsideBuilding, selectedFloor]);

  useEffect(() => {
    setSelectedFloorplanMarker(
      readFloorplanCoordinatesPoint(position.floorplanLat, position.floorplanLng, floorplanDimensions),
    );
  }, [floorplanDimensions, position.floorplanLat, position.floorplanLng]);

  async function handleNext() {
    if (!selectedFloor) {
      return;
    }

    const floorplanCoordinates =
      floorplanImageUrl && selectedFloorplanMarker
        ? planPointToFloorplanCoordinates(selectedFloorplanMarker, floorplanDimensions)
        : null;

    await onNext({
      building: selectedBuilding?.name ?? (isOutsideBuilding ? OUTSIDE_BUILDING_SELECT_VALUE : ''),
      buildingId: selectedBuildingId ?? null,
      floor: selectedFloor,
      floorplanBlobId: floorplanRecord?.pendingUpload ? floorplanRecord.id : null,
      floorplanLat: floorplanCoordinates?.lat ?? null,
      floorplanLng: floorplanCoordinates?.lng ?? null,
      floorplanNotPlacedReason: '',
      floorplanState: floorplanImageUrl
        ? floorplanCoordinates
          ? FLOORPLAN_PLACEMENT_STATES.placed
          : FLOORPLAN_PLACEMENT_STATES.notPlaced
        : FLOORPLAN_PLACEMENT_STATES.notAvailable,
    });
  }

  async function handleFloorplanUpload(file: File | null) {
    if (!file) {
      return;
    }

    setIsSavingFloorplan(true);
    setFloorplanUploadError(null);

    try {
      await saveLocalFloorplan(file);
      setSelectedFloorplanMarker(null);
    } catch (uploadError) {
      setFloorplanUploadError(
        uploadError instanceof Error
          ? uploadError.message
          : translateInventoryText(
              'app.inventory.position.floorplan.saveLocalError',
              'Unable to save floorplan locally.',
            ),
      );
    } finally {
      setIsSavingFloorplan(false);
    }
  }

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      {error ? <Alert message={error} showIcon type="error" /> : null}

      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        <BuildingSelect
          buildings={buildings}
          isLoading={isLoadingBuildings}
          onSelect={buildingId => {
            setSelectedBuildingId(buildingId);
            if (buildingId === null) {
              setSelectedFloor(GROUND_FLOOR_VALUE);
            }
            setSelectedFloorplanMarker(null);
            setFloorplanUploadError(null);
          }}
          selectedBuildingId={selectedBuildingId}
        />
        <FloorSelect
          disabled={selectedBuildingId === undefined || isOutsideBuilding}
          onSelect={value => {
            setSelectedFloor(value);
            setSelectedFloorplanMarker(null);
            setFloorplanUploadError(null);
          }}
          selectedFloor={selectedFloor}
        />
      </Space>

      {selectedFloor ? (
        <FloorplanPlacementPanel
          allowLocalFloorplanReplace
          emptyDescription={translateInventoryText(
            'app.inventory.position.floorplan.noFloorplanCanContinue',
            'No floorplan exists for this building and floor. You can continue without one.',
          )}
          floorplanDimensions={floorplanDimensions}
          floorplanImageUrl={floorplanImageUrl}
          floorplanName={floorplan?.name}
          floorplanRecord={floorplanRecord}
          existingPins={existingFloorplanPins}
          imageDescription={translateInventoryText(
            'app.inventory.position.floorplan.locationStepDescription',
            'Tap the floorplan to place this meter, or drag the marker to refine it.',
          )}
          isLoading={isLoadingFloorplan}
          isSavingFloorplan={isSavingFloorplan}
          onPickMarker={setSelectedFloorplanMarker}
          onUpload={handleFloorplanUpload}
          selectedMarker={selectedFloorplanMarker}
          uploadError={floorplanUploadError}
        />
      ) : null}

      <Flex gap="small" justify="space-between" wrap>
        <Button onClick={onBack} size="large">
          {translateInventoryText('app.inventory.position.actions.back', 'Back')}
        </Button>
        <Button disabled={!canContinue} onClick={() => void handleNext()} size="large" type="primary">
          {translateInventoryText('app.inventory.position.actions.next', 'Next')}
        </Button>
      </Flex>
    </Space>
  );
}

function BuildingSelect({
  buildings,
  isLoading,
  onSelect,
  selectedBuildingId,
}: {
  buildings: InventoryBuildingOption[];
  isLoading: boolean;
  onSelect: (buildingId: string | null | undefined) => void;
  selectedBuildingId: string | null | undefined;
}) {
  const value = selectedBuildingId === undefined ? '' : (selectedBuildingId ?? OUTSIDE_BUILDING_SELECT_VALUE);

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Typography.Text strong>
        {translateInventoryText('app.inventory.position.building.label', 'Building')}
      </Typography.Text>
      {isLoading ? (
        <Flex align="center" justify="center" style={{ minHeight: 72 }}>
          <Spin />
        </Flex>
      ) : (
        <select
          onChange={event => {
            const nextValue = event.currentTarget.value;
            onSelect(nextValue === '' ? undefined : nextValue === OUTSIDE_BUILDING_SELECT_VALUE ? null : nextValue);
          }}
          style={{ minHeight: 40, width: '100%' }}
          value={value}
        >
          <option value="">
            {translateInventoryText('app.inventory.position.building.placeholder', 'Select building')}
          </option>
          <option value={OUTSIDE_BUILDING_SELECT_VALUE}>
            {translateInventoryText('app.inventory.position.building.outsideNotSpecified', 'Outside / not specified')}
          </option>
          {buildings.map(building => (
            <option key={building.id} value={building.id}>
              {building.code ? `${building.name} (${building.code})` : building.name}
            </option>
          ))}
        </select>
      )}
    </Space>
  );
}

function FloorSelect({
  disabled,
  onSelect,
  selectedFloor,
}: {
  disabled: boolean;
  onSelect: (floor: string) => void;
  selectedFloor: string;
}) {
  const floorOptions = getDefaultBuildingFloorOptions();

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Typography.Text strong>{translateInventoryText('app.inventory.position.floor.label', 'Floor')}</Typography.Text>
      <select
        disabled={disabled}
        onChange={event => onSelect(event.currentTarget.value)}
        style={{ minHeight: 40, width: '100%' }}
        value={selectedFloor}
      >
        <option value="">{translateInventoryText('app.inventory.position.floor.placeholder', 'Select floor')}</option>
        {floorOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Space>
  );
}
